import {
    Component,
    DestroyRef,
    type ElementRef,
    effect,
    inject,
    input,
    output,
    signal,
    viewChild,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { TooltipModule } from '@openng/optimus-ui/tooltip';

import { AuthenticationService } from '@app/auth/services/authentication.service';
import { MarkdownLazyModule } from '@app/markdown-lazy.module';
import { AiChatMessagePartsComponent } from '@app/shared/components/ai-chat/ai-chat-message-parts/ai-chat-message-parts.component';
import type { ChatMessage } from '@app/shared/components/ai-chat/chat-message.model';

@Component({
    selector: 'cor-ai-chat-message-list',
    templateUrl: './ai-chat-message-list.component.html',
    styleUrls: ['./ai-chat-message-list.component.scss'],
    imports: [TooltipModule, MarkdownLazyModule, AiChatMessagePartsComponent],
})
export class AiChatMessageListComponent {
    private readonly destroyRef = inject(DestroyRef);
    private readonly scrollContainer = viewChild<ElementRef<HTMLElement>>('scrollContainer');

    readonly messages = input.required<ChatMessage[]>();
    readonly botName = input<string>('CORRA');
    readonly initialMessages = input<string[]>([]);
    readonly initialMessageClick = output<string>();
    readonly copyClick = output<string>();
    readonly retryClick = output<void>();

    protected readonly currentUser = toSignal(inject(AuthenticationService).currentUser$, {
        initialValue: null,
    });

    private previousMessageCount = 0;
    private resizeObserver?: ResizeObserver;
    private thinkingIntervalId?: ReturnType<typeof setInterval>;
    private readonly thinkingLabels = ['Thinking', 'Planning', 'Working'] as const;
    protected readonly thinkingLabelIndex = signal(0);

    constructor() {
        effect(() => {
            const msgs = this.messages();
            const isNewMessage = msgs.length > this.previousMessageCount;
            this.previousMessageCount = msgs.length;
            requestAnimationFrame(() => this.scrollToBottom({ force: isNewMessage }));
        });

        effect(() => {
            const container = this.scrollContainer();
            if (!container) {
                return;
            }

            this.resizeObserver?.disconnect();
            this.resizeObserver = new ResizeObserver(() => this.scrollToBottom({ force: false }));
            this.resizeObserver.observe(container.nativeElement);
        });

        effect(() => {
            const isThinking = this.messages().some(
                message =>
                    message.id === 'loading-message' ||
                    (message.id === 'streaming-message' &&
                        message.parts?.length === 0 &&
                        !message.text),
            );
            if (isThinking) {
                if (!this.thinkingIntervalId) {
                    this.thinkingIntervalId = setInterval(() => {
                        this.thinkingLabelIndex.update(
                            index => (index + 1) % this.thinkingLabels.length,
                        );
                    }, 2500);
                }
            } else if (this.thinkingIntervalId) {
                clearInterval(this.thinkingIntervalId);
                this.thinkingIntervalId = undefined;
                this.thinkingLabelIndex.set(0);
            }
        });

        this.destroyRef.onDestroy(() => {
            this.resizeObserver?.disconnect();
            if (this.thinkingIntervalId) {
                clearInterval(this.thinkingIntervalId);
            }
        });
    }

    protected thinkingLabel(): string {
        return this.thinkingLabels[this.thinkingLabelIndex()];
    }

    protected handleMarkdownReady(): void {
        requestAnimationFrame(() => {
            const container = this.scrollContainer()?.nativeElement;
            if (!container) {
                return;
            }
            container.querySelectorAll('a[href]').forEach((anchor: Element) => {
                if (!anchor.hasAttribute('target')) {
                    anchor.setAttribute('target', '_blank');
                    anchor.setAttribute('rel', 'noopener noreferrer');
                }
            });
        });
    }

    private scrollToBottom({ force }: { force: boolean }): void {
        const stickThresholdPx = 150;
        const el = this.scrollContainer()?.nativeElement;
        if (!el) {
            return;
        }

        if (!force) {
            const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
            if (distanceFromBottom > stickThresholdPx) {
                return;
            }
        }

        el.scrollTop = el.scrollHeight;
    }
}
